import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { secureGet, secureDelete } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

import { PrimaryButton } from '../../components/PrimaryButton';
import GenericCard from '../Users/GenericCard';
import PageScroller from '../../components/PageScroller';
import DeletePopup from '../../components/DeletePopup';
import Filter from '../../components/Filter';
import OptionsMenu from '../../components/OptionsMenu';
import ModuleWrapper from '../../components/ModuleWrapper';
import SnackbarNotification from '../../components/SnackbarNotification';
import TableLoadingArea from '../../components/TableLoadingArea';

import AddIcon from '../../icons/plusB.svg';
import EditIcon from '../../icons/edit.svg';
import DeleteIcon from '../../icons/trashR.svg';

const VehicleModels = () => {
  const location = useLocation();
  const [models, setModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [currentPageModels, setCurrentPageModels] = useState([]);
  const [pageSize, setPageSize] = useState(30);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const [filters, setFilters] = useState(() => {
    const id = location.state?.vehicleBrandId;
    return { needle: '', vehicleBrandId: id != null ? String(id) : '' };
  });
  const [brandOptions, setBrandOptions] = useState([]);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showModule, setShowModule] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);

  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { t } = useTranslation();
  const topRef = useRef(null);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const filterOptions = [
    { name: 'needle', type: 'search' },
    {
      name: 'vehicleBrandId',
      type: 'dropdown',
      label: t('fields.vehicleModels.brand'),
      options: [ ...brandOptions.map((b) => ({ value: b.id, label: b.name }))],
    },
  ];

  const paginateData = (data, page, size) => {
    const startIndex = page * size;
    const endIndex = startIndex + size;
    return data.slice(startIndex, endIndex);
  };

  const updatePagination = useCallback((data, page, size) => {
    setTotalPages(Math.ceil((data?.length || 0) / size) || 1);
    setTotalElements(data?.length || 0);
    setCurrentPageModels(paginateData(data || [], page, size));
  }, []);

  const fetchModels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await secureGet('/vehicle-models', showSnackbar, { signal });
      const list = res?.data || [];
      setModels(list);
      setFilteredModels(list);
      setCurrentPage(0);
      updatePagination(list, 0, pageSize);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error fetching vehicle models:', err);
    } finally {
      setLoading(false);
    }
  }, [signal, pageSize, updatePagination]);

  const fetchBrands = useCallback(async () => {
    try {
      const res = await secureGet('/vehicle-brands', showSnackbar, { signal });
      const list = res?.data || [];
      setBrandOptions(list);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error fetching vehicle brands:', err);
    }
  }, [signal]);

  useEffect(() => {
    const ac = new AbortController();
    fetchModels();
    return () => ac.abort();
  }, [fetchModels]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const applyFilters = useCallback(() => {
    let filtered = [...models];

    if (filters.needle) {
      const q = filters.needle.toLowerCase();
      filtered = filtered.filter((m) =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.brandName || '').toLowerCase().includes(q)
      );
    }

    if (filters.vehicleBrandId) {
      filtered = filtered.filter(
        (m) => String(m.vehicleBrandId || m.brand?.id || '') === String(filters.vehicleBrandId)
      );
    }

    setFilteredModels(filtered);
    setCurrentPage(0);
    updatePagination(filtered, 0, pageSize);
  }, [models, filters, pageSize, updatePagination]);

  useEffect(() => {
    applyFilters();
  }, [filters, models, applyFilters]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setCurrentPageModels(paginateData(filteredModels, page, pageSize));
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
    updatePagination(filteredModels, 0, newPageSize);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({ needle: '', vehicleBrandId: '' });
    setFilteredModels(models);
    updatePagination(models, 0, pageSize);
  };

  const handleAddClick = () => {
    setSelectedModel(null);
    setShowModule(true);
  };

  const handleEditClick = (model) => {
    setSelectedModel(model);
    setShowModule(true);
  };

  const handleDeleteClick = (model) => {
    setItemToDelete(model);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await secureDelete(`/vehicle-models/${itemToDelete.id}`, showSnackbar);
      await fetchModels();
      showSnackbar(t('confirmation.vehicleModelsDelete') || 'Modèle supprimé', 'success');
    } catch (e) {
      console.error('Error deleting vehicle model:', e);
    } finally {
      setDeleting(false);
      setDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

  const handleModuleClose = (action = 'cancel') => {
    if (action === 'submit') fetchModels();
    setShowModule(false);
    setSelectedModel(null);
  };

  const getMenuOptions = (model) => ([
    { label: t('actions.modify'), icon: EditIcon, action: () => handleEditClick(model) },
    { label: t('actions.delete'), icon: DeleteIcon, color: '#EF476F', action: () => handleDeleteClick(model) },
  ]);

  return (
    <div ref={topRef}>
      <Helmet>
        <title>Drivnest - {t('titles.vehicleModels') || 'Gestion des modèles de véhicules'}</title>
      </Helmet>

      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t('titles.vehicleModels') || 'Gestion des modèles de véhicules'}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t('primaryButtons.add_vehicleModel') || 'Ajouter un modèle'}
            icon={AddIcon}
            onClick={handleAddClick}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <TableLoadingArea loading={loading} minHeight={200} className="mt-6">
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {!loading && currentPageModels.map((model) => (
            <GenericCard
              key={model.id}
              title={model.name || ''}
              subtitle={model.brandName ? `${t('fields.vehicleModels.brand')}: ${model.brandName}` : ''}
              optionsMenu={
                <div onClick={(e) => e.stopPropagation()}>
                  <OptionsMenu options={getMenuOptions(model)} />
                </div>
              }
            />
          ))}
        </div>

        {!loading && currentPageModels.length === 0 && (
          <div className="text-center mt-4">
          </div>
        )}
      </TableLoadingArea>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
        />
      )}

      {showModule && (
        <ModuleWrapper
          moduleType="vehicle-models"
          mode={selectedModel ? 'modify' : 'add'}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedModel || {}}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default VehicleModels;
